import { QueryClient } from '@tanstack/react-query';

import { MessageStatus } from './queries/useSearchTickets/useSearchTickets.interface';

import {
    ApiResponse,
    Ticket,
} from 'modules/IMC/MessageCenter/components/MessageCenterContent/MessageList/MessageList.interface';

// Zendesk's `/users/:id/tickets/requested` can omit a ticket for a moment after it is created, so a
// refetch that lands in that window would drop it from the list. How long we keep re-adding it.
export const RECENTLY_CREATED_TICKET_TTL_MS = 60 * 1000;

interface RecentlyCreatedTicket {
    ticket: Ticket;
    createdAt: number;
    // Refetches the lists once the TTL is up, so a row Zendesk never returned doesn't linger until
    // the next poll. Cleared when a response returns the ticket first.
    expiryTimer: ReturnType<typeof setTimeout>;
}

// Keyed by Zendesk user id so an impersonation or account switch never sees another user's ticket.
const recentlyCreatedTickets = new Map<number, RecentlyCreatedTicket[]>();

export const sortByLastPublicComment = (a: Ticket, b: Ticket) => {
    const aTime = new Date(a.last_comment?.created_at ?? 0).getTime();
    const bTime = new Date(b.last_comment?.created_at ?? 0).getTime();
    return bTime - aTime;
};

// A list key ends in its statuses, or in 'all' for the shared list both tabs are derived from.
const listIncludesStatus = (queryKey: readonly unknown[], status: string) =>
    queryKey[3] === 'all' || (Array.isArray(queryKey[3]) && queryKey[3].includes(status as MessageStatus));

export function rememberCreatedTicket(queryClient: QueryClient, zdUserId: number, ticket: Ticket, now = Date.now()) {
    const existing = recentlyCreatedTickets.get(zdUserId) ?? [];
    existing.filter((entry) => entry.ticket.id === ticket.id).forEach((entry) => clearTimeout(entry.expiryTimer));

    const expiryTimer = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['messageCenterTickets', zdUserId, ''] });
    }, RECENTLY_CREATED_TICKET_TTL_MS);

    const entries = existing.filter((entry) => entry.ticket.id !== ticket.id);
    recentlyCreatedTickets.set(zdUserId, [...entries, { ticket, createdAt: now, expiryTimer }]);
}

/**
 * Puts `ticket` at the top of every cached requested-tickets list (no search query) whose statuses
 * include it. Lists that were never loaded are left alone: their first fetch picks the ticket up
 * through `mergeRecentlyCreatedTickets`.
 */
export function insertTicketIntoLists(queryClient: QueryClient, zdUserId: number, ticket: Ticket) {
    const queries = queryClient.getQueryCache().findAll({ queryKey: ['messageCenterTickets', zdUserId, ''] });

    queries.forEach((query) => {
        const data = query.state.data as ApiResponse | undefined;
        if (!data?.results || !listIncludesStatus(query.queryKey, ticket.status)) {
            return;
        }

        const others = data.results.filter((t) => t.id !== ticket.id);
        const count = data.count + (others.length === data.results.length ? 1 : 0);
        queryClient.setQueryData<ApiResponse>(query.queryKey, { ...data, results: [ticket, ...others], count });
    });
}

/**
 * Re-adds tickets created in this session that a requested-tickets response does not include yet.
 * An entry is dropped once a response includes it or it is older than the TTL. Returns `response`
 * itself when there is nothing to add, so a session that created no ticket is unaffected.
 */
export function mergeRecentlyCreatedTickets(
    queryClient: QueryClient,
    queryKey: readonly unknown[],
    response: ApiResponse,
    now = Date.now()
): ApiResponse {
    const [, zdUserId, searchQuery] = queryKey;
    if (typeof zdUserId !== 'number' || searchQuery) {
        return response;
    }

    const entries = recentlyCreatedTickets.get(zdUserId);
    if (!entries?.length) {
        return response;
    }

    const returnedIds = new Set(response.results.map((ticket) => ticket.id));
    const previous = queryClient.getQueryData<ApiResponse>(queryKey);

    const pending = entries.filter(
        (entry) => now - entry.createdAt < RECENTLY_CREATED_TICKET_TTL_MS && !returnedIds.has(entry.ticket.id)
    );
    entries.filter((entry) => !pending.includes(entry)).forEach((entry) => clearTimeout(entry.expiryTimer));
    if (pending.length) {
        recentlyCreatedTickets.set(zdUserId, pending);
    } else {
        recentlyCreatedTickets.delete(zdUserId);
    }

    // Prefer the cached copy so updates made since the create (last viewed, a reply) are kept.
    const missing = pending
        .filter((entry) => listIncludesStatus(queryKey, entry.ticket.status))
        .map((entry) => previous?.results?.find((t) => t.id === entry.ticket.id) ?? entry.ticket);
    if (!missing.length) {
        return response;
    }

    return {
        ...response,
        results: [...missing, ...response.results].sort(sortByLastPublicComment),
        count: response.count + missing.length,
    };
}
